import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { useT } from '../../i18n/LanguageContext';
import useShippingRequest from '../../hooks/useShippingRequest';

// IVR documents remain in private storage. This component requests a short-lived URL through the
// record-authorized endpoint and never receives or exposes the underlying object key.
export function SignedDoc({ recordId, docRef, className, title, children, adminDistributorId }) {
  const t = useT();
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const linkRequest = useRef(null);
  const { begin, version } = useShippingRequest(
    `${adminDistributorId || ''}:${recordId}:${docRef}`
  );
  useEffect(() => {
    let active = true;
    const request = begin();
    const current = () => active && request.isCurrent();
    if (!recordId || !docRef) {
      setUrl('');
      return undefined;
    }
    setUrl('');
    setFailed(false);
    api
      .get(`/ivr/${encodeURIComponent(recordId)}/document`, {
        params: {
          ref: docRef,
          ...(adminDistributorId
            ? { businessContext: 'distributor', distributorId: adminDistributorId }
            : {}),
        },
        signal: request.signal,
        sessionBinding: request.sessionBinding,
      })
      .then((res) => {
        if (!current()) return;
        const value = res.data?.data?.url || res.data?.url || '';
        if (typeof value !== 'string') throw new Error('Invalid document link');
        const parsed = new URL(value);
        if (parsed.protocol !== 'https:' || parsed.username || parsed.password)
          throw new Error('Invalid document link');
        linkRequest.current = request;
        setUrl(value);
      })
      .catch(() => {
        if (current()) {
          setUrl('');
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [recordId, docRef, retry, version, begin, adminDistributorId]);
  const availableUrl = linkRequest.current?.isCurrent() ? url : '';
  return (
    <div>
      <a
        href={availableUrl || undefined}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        title={availableUrl ? title : t('Preparing secure link…')}
        aria-disabled={!availableUrl}
        tabIndex={availableUrl ? 0 : -1}
        onClick={(e) => {
          if (!availableUrl || !linkRequest.current?.isCurrent()) e.preventDefault();
        }}
      >
        {children(availableUrl)}
      </a>
      {failed && (
        <div role="alert" className="mt-1 text-xs text-red-700">
          <p>{t('Could not prepare this document.')}</p>
          <button
            type="button"
            className="underline"
            onClick={() => setRetry((value) => value + 1)}
          >
            {t('Retry document')}
          </button>
        </div>
      )}
    </div>
  );
}

export function ApprovalDocumentSection({
  selectedFile,
  onFileChange,
  onRemoveFile,
  onCancel,
  onConfirm,
  loading,
  confirmLabel,
  fileOptional = false,
  // showNote RENDERS the note field; noteRequired GATES the confirm on it. Separate props so a note
  // can be offered without being mandatory.
  showNote = false,
  noteRequired = false,
  adminNote = '',
  onNoteChange,
}) {
  const t = useT();
  const fileInputRef = useRef(null);

  const handleSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileChange(file);
    e.target.value = '';
  };

  const hasNote = !!(adminNote || '').trim();
  const hasFile = !!selectedFile;
  // Two INDEPENDENT gates, flattened out of the old nested ternary. That ternary only consulted
  // fileOptional inside the noteRequired branch, so fileOptional silently did nothing on a no-note
  // path - which is how "Covered" became impossible to complete without a PDF. Flat, so the
  // combinations cannot drift apart again.
  const canConfirm = (fileOptional || hasFile) && (!noteRequired || hasNote);

  return (
    <div className="border border-dashed border-[#007a55] rounded-[10px] bg-[rgba(222,252,237,0.15)] p-4 mb-4">
      <p className="text-[14px] font-semibold text-[#007a55] mb-1">
        {fileOptional ? t('Attach Document (optional)') : t('Attach Document')}
      </p>
      <p className="text-[12px] text-[#64748b] mb-3">
        {noteRequired
          ? t('Add a note explaining your decision below. A document is optional.')
          : fileOptional
            ? t('A document and a note are both optional - you can confirm without either.')
            : t('Please attach the document to finalize this request.')}
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileInput}
        className="hidden"
      />

      {!selectedFile ? (
        <button
          onClick={handleSelectClick}
          disabled={loading}
          className="w-full h-[40px] border border-dashed border-[#007a55] rounded-[8px] bg-white flex items-center justify-center gap-2 hover:bg-[#f0fdf4] transition-colors mb-3"
        >
          <span className="material-symbols-outlined text-[#007a55] text-[18px]">attach_file</span>
          <span className="text-[13px] font-medium text-[#007a55]">
            {fileOptional ? t('Select Document (Optional)') : t('Select PDF Document')}
          </span>
        </button>
      ) : (
        <div className="w-full h-[40px] border border-dashed border-[#007a55] rounded-[8px] bg-white flex items-center justify-between px-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[#007a55] text-[18px]">
              description
            </span>
            <span className="text-[13px] font-medium text-[#0f172a] truncate max-w-[200px]">
              {selectedFile.name}
            </span>
            <span className="text-[11px] text-[#64748b]">
              ({(selectedFile.size / 1024).toFixed(0)} KB)
            </span>
          </div>
          <button
            type="button"
            onClick={onRemoveFile}
            disabled={loading}
            aria-label={t('Remove {name}', { name: selectedFile.name })}
            className="text-[#64748b] hover:text-[#f23e41] transition-colors disabled:opacity-50"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
              close
            </span>
          </button>
        </div>
      )}

      {(showNote || noteRequired) && (
        <div className="mb-3">
          <p className="text-[12px] font-medium text-[#0f172a] mb-1">
            {t('Note from MedEffects')}{' '}
            {noteRequired ? (
              <span className="text-[#f23e41]">*</span>
            ) : (
              <span className="text-[11px] font-normal text-[#64748b]">{t('(optional)')}</span>
            )}
          </p>
          <textarea
            aria-label={t('Note from MedEffects')}
            value={adminNote}
            onChange={(e) => onNoteChange && onNoteChange(e.target.value)}
            placeholder={t('Explain why this IVR is not covered...')}
            className="w-full h-[70px] border border-[#d6dce8] rounded-[8px] px-3 py-2 text-[13px] text-[#0f172a] placeholder:text-[#97a3b6] resize-none focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
          />
          {noteRequired && !hasNote && (
            <p className="text-[11px] text-[#f23e41] mt-1">{t('A note is required.')}</p>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="text-[13px] font-medium text-[#64748b] hover:text-[#0f172a] transition-colors"
        >
          {t('Cancel')}
        </button>
        <button
          onClick={onConfirm}
          disabled={!canConfirm || loading}
          className={`h-[32px] px-4 rounded-[6px] text-[13px] font-medium transition-colors ${
            canConfirm && !loading
              ? 'bg-[#0f172a] text-white hover:bg-[#1e293b]'
              : 'bg-[#e2e8f0] text-[#97a3b6] cursor-not-allowed'
          }`}
        >
          {loading ? t('Uploading...') : t(confirmLabel || 'Confirm')}
        </button>
      </div>
    </div>
  );
}
